import { App, Form, Modal } from 'antd';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { useQuarantineDefectV1ControllerUpdateDefectDetails } from 'generated/apiComponents';
import { QuarantineDefectOutputDto } from 'generated/apiSchemas';
import {
  Module_Enum,
  useGetSegmentHistoryOptionsQuery,
} from 'generated/graphql';
import { isUndefinedOrNull } from 'technical/utils/is-null-or-undefined';

import { UpdateDefectFormValues } from './types';
import { UpdateDefectForm } from './update-defect-form';

interface UpdateDefectModalProps {
  isModalVisible: boolean;
  closeModal: () => void;
  defect: QuarantineDefectOutputDto | null;
  zone: string;
  refetchData: () => void;
}

export const UpdateDefectModal: React.FC<UpdateDefectModalProps> = ({
  isModalVisible,
  closeModal,
  defect,
  zone,
  refetchData,
}) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<UpdateDefectFormValues>();
  const { currentConstructionSite } = useAppContext();
  invariant(currentConstructionSite, 'No constructionSite');

  const { mutate: updateQuarantineDefect } =
    useQuarantineDefectV1ControllerUpdateDefectDetails();

  const save = async (row: UpdateDefectFormValues) => {
    updateQuarantineDefect(
      {
        pathParams: {
          defectId: defect?.id ?? '',
        },
        body: {
          type: row.type,
          date: row.date.utc().toISOString(),
          zone: row.zone,
          description: row.description,
        },
      },
      {
        onSuccess: async () => {
          refetchData();
          closeModal();

          message.open({
            type: 'success',
            content: t(
              'qualityControl.segmentHistory.drawer.defectModal.update.success',
            ),
          });
        },
        onError: () => {
          message.open({
            type: 'error',
            content: t(
              'qualityControl.segmentHistory.drawer.defectModal.update.error',
            ),
          });
        },
      },
    );
  };

  const graphqlHeaders = useGraphqlApiHeaders(Module_Enum.QualityScan);
  const { data, loading } = useGetSegmentHistoryOptionsQuery({
    variables: {
      constructionSiteId: currentConstructionSite.id,
      projectId: currentConstructionSite.projectId,
    },
    context: { headers: graphqlHeaders },
  });

  const typeOptions = useMemo(() => {
    const options =
      data?.defectTypes?.map((type) => ({
        label: type.label,
        value: type.id,
      })) || [];

    return options;
  }, [data]);

  const currentType = typeOptions.find((type) => type.label === defect?.type);

  const zoneOptions = useMemo(() => {
    const options =
      data?.scanSites
        ?.filter((site) => site.isReceiptActivated === true)
        ?.map((site) => ({
          label: site.label,
          value: site.id,
        })) || [];

    return options;
  }, [data]);

  const initialDefectValues = {
    description: defect?.description ?? '',
    date: dayjs(defect?.createdAt).tz().second(0).millisecond(0),
    zone,
    type: currentType?.value,
  };

  return (
    <Modal
      title={t('qualityControl.segmentHistory.drawer.defectModal.title')}
      open={isModalVisible}
      onOk={form.submit}
      onCancel={closeModal}
      destroyOnHidden
    >
      {!isUndefinedOrNull(defect) ? (
        <UpdateDefectForm
          form={form}
          save={save}
          initialValues={initialDefectValues}
          typeOptions={typeOptions}
          zoneOptions={zoneOptions}
          loading={loading}
        />
      ) : null}
    </Modal>
  );
};
